import React, { useState, useEffect } from 'react';
import { Save, Plus, Eye, TrendingUp } from 'lucide-react';
import { apiService } from '../../services/api';

interface AvaliacaoFisicaData {
  peso: string;
  altura: string;
  circunferenciaCintura: string;
  circunferenciaQuadril: string;
  circunferenciaBraco: string;
  percentualGordura: string;
  massaMuscular: string;
  pressaoArterial: string;
  frequenciaCardiaca: string;
  observacoes: string;
}

const AvaliacaoFisica: React.FC = () => {
  const [formData, setFormData] = useState<AvaliacaoFisicaData>({
    peso: '',
    altura: '',
    circunferenciaCintura: '',
    circunferenciaQuadril: '',
    circunferenciaBraco: '',
    percentualGordura: '',
    massaMuscular: '',
    pressaoArterial: '',
    frequenciaCardiaca: '',
    observacoes: ''
  });
  const [loading, setLoading] = useState(false);
  const [historico, setHistorico] = useState<any[]>([]);
  const [pacienteId] = useState('672c8f123456789012345678'); // ID fixo para teste

  useEffect(() => {
    carregarHistorico();
  }, []);

  const carregarHistorico = async () => {
    try {
      const data = await apiService.getAvaliacoes(pacienteId, 'fisica');
      setHistorico(data);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      // Manter dados mock em caso de erro
      setHistorico([
        {
          _id: '1',
          createdAt: '2024-10-15',
          avaliacaoFisica: { peso: 75.5, imc: 27.7 }
        }
      ]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calcularIMC = () => {
    const peso = parseFloat(formData.peso);
    const altura = parseFloat(formData.altura) / 100; // converter cm para m
    
    if (peso && altura) {
      return (peso / (altura * altura)).toFixed(1);
    }
    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        paciente: pacienteId,
        tipo: 'fisica',
        avaliacaoFisica: {
          peso: parseFloat(formData.peso),
          altura: parseFloat(formData.altura),
          circunferencias: {
            cintura: parseFloat(formData.circunferenciaCintura) || 0,
            quadril: parseFloat(formData.circunferenciaQuadril) || 0,
            braco: parseFloat(formData.circunferenciaBraco) || 0
          },
          percentualGordura: parseFloat(formData.percentualGordura) || 0,
          massaMuscular: parseFloat(formData.massaMuscular) || 0,
          pressaoArterial: formData.pressaoArterial,
          frequenciaCardiaca: parseFloat(formData.frequenciaCardiaca) || 0,
          observacoes: formData.observacoes
        }
      };
      
      await apiService.createAvaliacao(payload);
      
      // Recarregar histórico
      await carregarHistorico();
      
      // Limpar formulário
      setFormData({
        peso: '',
        altura: '',
        circunferenciaCintura: '',
        circunferenciaQuadril: '',
        circunferenciaBraco: '',
        percentualGordura: '',
        massaMuscular: '',
        pressaoArterial: '',
        frequenciaCardiaca: '',
        observacoes: ''
      });

      alert('Avaliação física salva com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar avaliação:', error);
      alert('Erro ao salvar avaliação. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid p-4">
      <div className="row">
        <div className="col-lg-8">
          <div className="card shadow-sm">
            <div className="card-header bg-success text-white">
              <h4 className="mb-0">📊 Nova Avaliação Física</h4>
              <small>Registre suas medidas e dados físicos</small>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                {/* Medidas Básicas */}
                <div className="row mb-4">
                  <div className="col-12">
                    <h6 className="text-success mb-3">Medidas Básicas</h6>
                  </div>
                  <div className="col-md-3 mb-3">
                    <label htmlFor="peso" className="form-label">Peso (kg) *</label>
                    <input
                      id="peso"
                      type="number"
                      step="0.1"
                      className="form-control"
                      name="peso"
                      value={formData.peso}
                      onChange={handleInputChange}
                      placeholder="75.5"
                      required
                    />
                  </div>
                  <div className="col-md-3 mb-3">
                    <label htmlFor="altura" className="form-label">Altura (cm) *</label>
                    <input
                      id="altura"
                      type="number"
                      className="form-control"
                      name="altura"
                      value={formData.altura}
                      onChange={handleInputChange}
                      placeholder="165"
                      required
                    />
                  </div>
                  <div className="col-md-3 mb-3">
                    <label htmlFor="imc" className="form-label">IMC</label>
                    <input
                      id="imc"
                      type="text"
                      className="form-control bg-light"
                      value={calcularIMC()}
                      readOnly
                      placeholder="Calculado automaticamente"
                    />
                  </div>
                  <div className="col-md-3 mb-3">
                    <label htmlFor="pressao-arterial" className="form-label">Pressão Arterial</label>
                    <input
                      id="pressao-arterial"
                      type="text"
                      className="form-control"
                      name="pressaoArterial"
                      value={formData.pressaoArterial}
                      onChange={handleInputChange}
                      placeholder="120/80"
                    />
                  </div>
                </div>

                {/* Circunferências */}
                <div className="row mb-4">
                  <div className="col-12">
                    <h6 className="text-success mb-3">Circunferências (cm)</h6>
                  </div>
                  <div className="col-md-4 mb-3">
                    <label htmlFor="cintura" className="form-label">Cintura</label>
                    <input
                      id="cintura"
                      type="number"
                      step="0.1"
                      className="form-control"
                      name="circunferenciaCintura"
                      value={formData.circunferenciaCintura}
                      onChange={handleInputChange}
                      placeholder="85.0"
                    />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label htmlFor="quadril" className="form-label">Quadril</label>
                    <input
                      id="quadril"
                      type="number"
                      step="0.1"
                      className="form-control"
                      name="circunferenciaQuadril"
                      value={formData.circunferenciaQuadril}
                      onChange={handleInputChange}
                      placeholder="98.0"
                    />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label htmlFor="braco" className="form-label">Braço</label>
                    <input
                      id="braco"
                      type="number"
                      step="0.1"
                      className="form-control"
                      name="circunferenciaBraco"
                      value={formData.circunferenciaBraco}
                      onChange={handleInputChange}
                      placeholder="32.0"
                    />
                  </div>
                </div>

                {/* Composição Corporal */}
                <div className="row mb-4">
                  <div className="col-12">
                    <h6 className="text-success mb-3">Composição Corporal</h6>
                  </div>
                  <div className="col-md-4 mb-3">
                    <label htmlFor="percentual-gordura" className="form-label">% Gordura</label>
                    <input
                      id="percentual-gordura"
                      type="number"
                      step="0.1"
                      className="form-control"
                      name="percentualGordura"
                      value={formData.percentualGordura}
                      onChange={handleInputChange}
                      placeholder="28.5"
                    />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label htmlFor="massa-muscular" className="form-label">Massa Muscular (kg)</label>
                    <input
                      id="massa-muscular"
                      type="number"
                      step="0.1"
                      className="form-control"
                      name="massaMuscular"
                      value={formData.massaMuscular}
                      onChange={handleInputChange}
                      placeholder="54.0"
                    />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label htmlFor="frequencia-cardiaca" className="form-label">Freq. Cardíaca (bpm)</label>
                    <input
                      id="frequencia-cardiaca"
                      type="number"
                      className="form-control"
                      name="frequenciaCardiaca"
                      value={formData.frequenciaCardiaca}
                      onChange={handleInputChange}
                      placeholder="72"
                    />
                  </div>
                </div>

                <div className="row mb-4">
                  <div className="col-12">
                    <label htmlFor="observacoes-avaliacao" className="form-label">Observações</label>
                    <textarea
                      id="observacoes-avaliacao"
                      className="form-control"
                      name="observacoes"
                      value={formData.observacoes}
                      onChange={handleInputChange}
                      rows={3}
                      placeholder="Observações sobre a avaliação, como se sente, dificuldades..."
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-2">
                  <button 
                    type="submit" 
                    className="btn btn-success d-flex align-items-center gap-2"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <output className="spinner-border spinner-border-sm">
                          <span className="visually-hidden">Salvando...</span>
                        </output>
                        Salvando...
                      </>
                    ) : (
                      <>
                        <Save size={20} />
                        Salvar Avaliação
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-lg-4">
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-light">
              <h5 className="mb-0">📈 Histórico de Avaliações</h5>
            </div>
            <div className="card-body">
              {historico.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <Plus size={48} className="mb-2" />
                  <p>Nenhuma avaliação registrada</p>
                </div>
              ) : (
                <div className="list-group list-group-flush">
                  {historico.map((item: any) => (
                    <div key={item._id} className="list-group-item border-0 px-0">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">Avaliação Física</h6>
                          <p className="mb-1 text-muted small">
                            Peso: {item.avaliacaoFisica?.peso || 0}kg | IMC: {item.avaliacaoFisica?.imc?.toFixed(1) || 0}
                          </p>
                          <small className="text-success">Concluída</small>
                        </div>
                        <div className="d-flex flex-column align-items-end">
                          <small className="text-muted mb-1">{new Date(item.createdAt).toLocaleDateString('pt-BR')}</small>
                          <button className="btn btn-sm btn-outline-primary">
                            <Eye size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Card de Evolução */}
          <div className="card shadow-sm">
            <div className="card-header bg-info text-white">
              <h6 className="mb-0">
                <TrendingUp size={16} className="me-2" />
                Evolução
              </h6>
            </div>
            <div className="card-body">
              <div className="text-center">
                <div className="mb-2">
                  <span className="badge bg-success fs-6">-2.3kg</span>
                </div>
                <small className="text-muted">Desde a primeira avaliação</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvaliacaoFisica;